import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import {
    getListProjectInviteSupported,
    getProjectById,
    getProjectIdListUserIsB2C,
    getProjectListUserIsB2C,
    getSelectedProjectDetailById
} from '@/service/projectService';
import { getViewDefaultsChatAI } from '@/component/ticketKnowledge/handler/ViewHandler';
import { getCorporationById } from '@/service/organizationService';
import { createNewAIChatThread } from '@/service/chatAIService';
import { v4 as uuidv4 } from 'uuid';
import { I18n } from 'aws-amplify';
import { hideLoadingMouse } from '@/common/utils/CustomCursor';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { getUserPermissionToViewKnowledge } from '@/service/userService';

export const isDisplayCustomerTicketList = (projectInuseDetail) => {
    return !Utils.isProjectGuestDesk(projectInuseDetail);
}

export const isDisplayHelpdeskTicketList = (projectInuseDetail) => {
    const isDisplayNone = Utils.isProjectGuest(projectInuseDetail) ||
        [Constant.PROJECT_TYPE.BASE, Constant.PROJECT_TYPE.CUSTOMER].includes(projectInuseDetail.type)
    return !isDisplayNone;
}

export const isDisplayKnowledgeMenu = async (corporationDetail, projectInuseDetail) => {
    const userId = localStorageUtil.getUserDetailId();
    const userPermission = await getUserPermissionToViewKnowledge(corporationDetail, projectInuseDetail, userId);

    const isMemberPermission = userPermission.type === Constant.USER_PERMISSION_VIEW_KNOWLEDGE.MEMBER_OF_PROJECT
    if (isMemberPermission) return true;

    const isKnowledgeServiceOff = userPermission.knowledgeService === Constant.STATUS_SERVICE_PERMISSION.OFF;
    if (isKnowledgeServiceOff) return false;

    if (userPermission.type === Constant.USER_PERMISSION_VIEW_KNOWLEDGE.B2C) {
        const projectIdListUserIsB2C = await getProjectIdListUserIsB2C(null, userId);
        return !Utils.isEmptyArray(projectIdListUserIsB2C);
    }
    
    return true;
}

export const isDisplayOpenChatAI = async (
    API,
    userDetail,
    corporationDetail,
    projectInuseDetail,
    projectIdListUserIsB2C
) => {
    const userPermissions = Utils.checkUserPermissionKnowledgeService(corporationDetail, projectInuseDetail);
    switch (userPermissions) {
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.PERSONAL_PROJECT:
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.MEMBER_OF_PROJECT:
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_PARTNER:
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_SUPPORTED:
            return await checkedUsingChatAI(API, corporationDetail, projectInuseDetail);
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.B2C: {
            const limitValue = corporationDetail?.limitValue;  
            if (!limitValue.usingChatAI) {
                return false;
            }
            const isEmptyArrayProjectIdListUserIsB2C = Utils.isEmptyArray(projectIdListUserIsB2C)
            return !isEmptyArrayProjectIdListUserIsB2C;
        }
        default:
            return false;
    }
};

export const isDisplayOpenChatAIFromMenu = (
    corporationDetail,
    projectInuseDetail,
) => {
    const userPermissions = Utils.checkUserPermissionKnowledgeService(corporationDetail, projectInuseDetail);
    switch (userPermissions) {
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.PERSONAL_PROJECT:
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.MEMBER_OF_PROJECT: {
            const limitValue = corporationDetail?.limitValue;               
            return limitValue.usingChatAI;
        }
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_PARTNER:
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_SUPPORTED:
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.B2C:
            return false;
        default:
            return false;
    }
};

export const isDisplayIconChatAIOnKnowledgeList = async (
    API,
    userPermissionView,
    corporationDetail,
    projectInuseDetail,
    organizationProject,
) => {
    projectInuseDetail = await getProjectById(API, projectInuseDetail.id);

    const limitValue = corporationDetail.limitValue;
    const usingChatAI = limitValue?.usingChatAI;

    switch (organizationProject.value) {
        case Constant.KNOWLEDGE_VIEW_OPTION.PRJ_IN_USE: {
            const contractType = corporationDetail.contractType;
            if (contractType === Constant.CORPORATION_CONTRACT_TYPE.PREMIUM) {
                if (!usingChatAI) {
                    return false;
                }
            } else if(!corporationDetail.inviterUsingChatAI) {
                return false;
            }
            return checkedUsageRangeProject(projectInuseDetail);
        }
        case Constant.KNOWLEDGE_VIEW_OPTION.ALL_KNOWLEDGE_IN_ORG:
            return usingChatAI;
        default:
            break;
    }
    const projectSelectedId = organizationProject.value;
    const internalProjectList = organizationProject.internalProjectList ? organizationProject.internalProjectList : [];
    const externalProjectList = organizationProject.externalProjectList ? organizationProject.externalProjectList : [];

    const listProjects = [...internalProjectList, ...externalProjectList];

    const projectSelectedInfo = listProjects.find(project => project.id === projectSelectedId);
    if (Utils.isEmptyObject(projectSelectedInfo)) {
        return false;
    }

    if (projectSelectedInfo?.contractType === Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK) {
        if (projectSelectedInfo?.inviterUsingChatAI === Constant.STATUS_SERVICE_PERMISSION.OFF) {
            return false;
        }
    }

    if(projectSelectedInfo?.contractType === Constant.CORPORATION_CONTRACT_TYPE.PREMIUM) {
        if(projectSelectedInfo?.limitValue?.usingChatAI === Constant.STATUS_SERVICE_PERMISSION.OFF) {
            return false;
        }
    }

    let range =
        projectSelectedInfo.affiliationOrganization === corporationDetail.id
            ? Constant.AI_SETTING_RANGE.IN_ORG
            : Constant.AI_SETTING_RANGE.OUT_ORG;

    if (userPermissionView.type === Constant.USER_PERMISSION_VIEW_KNOWLEDGE.B2C) {
        if (!usingChatAI) {
            return false;
        }
        range = Constant.AI_SETTING_RANGE.OUT_ORG;
    }

    return checkedUsageRangeProject(projectSelectedInfo, range);
}; 

export const checkedUsageRangeProject = (project, range = Constant.AI_SETTING_RANGE.IN_PROJECT) => {
    if (Utils.isEmptyObject(project)) {
        return false;
    }

    const projectSettings = Utils.convertJSONDataToObject(project?.projectSetting);
    const usageRangeInuse = projectSettings?.chatAISetting?.usageRange;
    if (Utils.isEmptyArray(usageRangeInuse)) {
        return false;
    }

    return usageRangeInuse.includes(range);
};

export const getFirstProjectWhenOpenChatAI = async (API, userDetail, corporationDetail, projectInuseDetail) => {
    
    let projectSelectedId = '';
    const userPermissions = Utils.checkUserPermissionKnowledgeService(corporationDetail, projectInuseDetail);
    switch (userPermissions) {
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.PERSONAL_PROJECT:
            projectSelectedId = Constant.KNOWLEDGE_VIEW_OPTION.ALL_KNOWLEDGE_IN_ORG;
            break;
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.MEMBER_OF_PROJECT:
            if (checkedUsageRangeProject(projectInuseDetail)) {
                projectSelectedId = projectInuseDetail.id;
            }
            break;
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_PARTNER: {
            const corpInviter = await getCorporationById(API, corporationDetail?.inviter);
            if (Utils.isEmptyObject(corpInviter)) {
                return '';
            }
            if (checkedUsageRangeProject(projectInuseDetail)) {
                projectSelectedId = projectInuseDetail.id;
            }
            break;
        }
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.B2C: {
            let res = await getProjectListUserIsB2C(API, userDetail.id);
            const projectList = res.projectList;

            if (Utils.isEmptyArray(projectList)) {
                return '';
            } else {
                let listProject = await Promise.all(
                    projectList.map(async id => await getProjectById(API, id))
                );
                listProject = listProject.filter(
                    project => project.active === Constant.ACTIVE_FLAG.ACTIVE
                );
                if (Utils.isEmptyArray(listProject)) {
                    return '';
                } else {
                    listProject = Utils.sortListByNameDictionaryASC(listProject);
                    if (
                        checkedUsageRangeProject(listProject[0], Constant.AI_SETTING_RANGE.OUT_ORG)
                    ) {
                        projectSelectedId = listProject[0]?.id;
                    }
                }
            }
            break;
        }
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_SUPPORTED: {
            let listProjectInvite = await getListProjectInviteSupported(API, projectInuseDetail.id);
            if (Utils.isEmptyArray(listProjectInvite)) {
                return '';
            } else {
                let listProject = await Promise.all(
                    listProjectInvite.map(async id => await getProjectById(API, id))
                );
                listProject = listProject.filter(
                    project => project.active === Constant.ACTIVE_FLAG.ACTIVE
                );
                if (Utils.isEmptyArray(listProject)) {
                    return '';
                } else {
                    listProject = Utils.sortListByNameDictionaryASC(listProject);
                    const firstProject = listProject[0];
                    const corporationInvited = await getCorporationById(
                        API,
                        firstProject.affiliationOrganization
                    );
                    if (Utils.isEmptyObject(corporationInvited)) {
                        return false;
                    }
                    if (checkedUsageRangeProject(firstProject, Constant.AI_SETTING_RANGE.OUT_ORG)) {
                        projectSelectedId = firstProject?.id;
                    }
                }
            }
            break;
        }
        default:
            return '';
    }
    return projectSelectedId;
};

export const handleOpenPanelOpenChatAI = async (
    API,
    panelList,
    userDetail,
    corporationDetail,
    projectInuseDetail,
    addNewPanel,
    userPermission,
    props,
    setOpeningTabChatAi = () => {},
    dataLimited = {}
) => {
    try {
    // showLoadingMouse();
    const { isLimited, panelDataChatAI, enqueueSnackbar } = dataLimited;
    let isExistPanelKnowledgeList = panelList.find(
        panel => panel.type === Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST
    );

    if (isExistPanelKnowledgeList) {
        let projectSelectedKnowledgeId = await getFirstProjectWhenOpenChatAI(
            API,
            userDetail,
            corporationDetail,
            projectInuseDetail
        );

        let [listViews, projectSelectedDetail] = await Promise.all([getViewDefaultsChatAI(API, projectSelectedKnowledgeId, userDetail), getSelectedProjectDetailById(API, projectSelectedKnowledgeId, projectInuseDetail)]);
        let firstView = {};
        let structure = {};
        if(!Utils.isEmpty(listViews)) {
            firstView = listViews[0];
            let visibility = getVisibilityDefault(corporationDetail, projectInuseDetail)
            structure = Utils.convertJSONDataToObject(firstView?.structure);
            structure.visibility = visibility;
        }

        if(projectSelectedKnowledgeId === Constant.KNOWLEDGE_VIEW_OPTION.ALL_KNOWLEDGE_IN_ORG) {
            firstView ={
                structure: {
                    ...Constant.DEFAULT_VIEW_STRUCTURE,
                    visibility: Constant.VISIBILITY_SETTING_TYPE.CUSTOMER
                }
            }
        }

        const newThreadId = `ai_chat_thread_${uuidv4()}`;
        const filterKnowledgeListData = {
            viewSelectedId: firstView?.id,
            viewSelected: firstView,
            viewStructure: structure,
            projectIdKnowledgeSelected: projectSelectedKnowledgeId,
            projectSelectedId: projectSelectedKnowledgeId,
            resetSearchCondition: true,
            usingBasicPrompt: true,
            isInvalidPrecondition: false,
            threadId: newThreadId,
            chatThreadId: newThreadId,
            ticketCreatedProjectId: structure?.ticketCreatedProjectId || '',
            ticketCreatedCorporationId: !Utils.isEmptyArray(listViews) ? projectSelectedDetail?.affiliationOrganization : corporationDetail?.id,
        }

        if (isLimited) {
            enqueueSnackbar(I18n.get('MSG-CM-WARNING-LIMIT-TAB'), {
                variant: 'error',
                autoHideDuration: 3000,
                style: { whiteSpace: 'pre-line' }
            });
            const tabList = panelDataChatAI?.tabList || [];
            if (!Utils.isEmptyArray(tabList) && !panelDataChatAI?.isDisplay) {
                const lastTab = tabList[tabList.length - 1];
                setOpeningTabChatAi(lastTab.id);
                addNewPanel({
                    type: Constant.PANEL_TYPE.OPEN_CHAT_AI,
                    tabId: lastTab.id,
                    filterKnowledgeListData: {
                        ...filterKnowledgeListData,
                        threadId: lastTab.chatThreadId,
                        chatThreadId: lastTab.chatThreadId
                    },
                    chatThreadId: lastTab.chatThreadId,
                    props
                });
            }
            hideLoadingMouse();
            return;
        }

        const chatThreadDto = {
            id: newThreadId,
            chatThreadId: newThreadId,
            corporationId: corporationDetail.id,
            affiliationProject: projectSelectedKnowledgeId,
            clientUUId: `client_uuid_${uuidv4()}`,
            creatorId: userDetail.id,
            metaData: { filterKnowledgeListData: JSON.stringify(filterKnowledgeListData) }
        }

        const newThreadChat = await createNewAIChatThread(chatThreadDto);
        if(!Utils.isEmptyObject(newThreadChat)) {
            let tabId = `${Constant.TAB_PANEL_TYPE.NEW_TAB_CHAT_AI}_${Date.now().toString()}`;
            setOpeningTabChatAi(tabId);
            addNewPanel({
                type: Constant.PANEL_TYPE.OPEN_CHAT_AI,
                tabId: tabId,
                filterKnowledgeListData: {
                    viewSelectedId: firstView?.id,
                    viewSelected: firstView,
                    viewStructure: structure,
                    projectIdKnowledgeSelected: projectSelectedKnowledgeId,
                    projectSelectedId: projectSelectedKnowledgeId,
                    resetSearchCondition: true,
                    usingBasicPrompt: true,
                    isInvalidPrecondition: false,
                    threadId: newThreadChat.id,
                    ticketCreatedProjectId: structure?.ticketCreatedProjectId || '',
                    ticketCreatedCorporationId: !Utils.isEmptyArray(listViews) ? projectSelectedDetail?.affiliationOrganization : corporationDetail?.id,
                },
                chatThreadId: newThreadId,
                props
            });
        }
    } else {
        addNewPanel({
            type: Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST,
            tabId: 'knowledge_ticket_list_tab',
            visibilitySettingType: Constant.VISIBILITY_SETTING_TYPE.PRIVATE,
            projectSelectedId: projectInuseDetail.id,
            corporationSelectedId: projectInuseDetail.affiliationOrganization,
            isCustomer: false,
            customerId: '',
            isOpenFromChatAI: true,
            props
        });
    }
    hideLoadingMouse();
    } catch (error) {
        console.log('✅ >>> handleOpenPanelOpenChatAI error :', error );
    }
};

export const getVisibilityDefault = (corporationDetail, projectInuseDetail) => {
    const userPermissions = Utils.checkUserPermissionKnowledgeService(corporationDetail, projectInuseDetail);
    switch (userPermissions) {
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.PERSONAL_PROJECT:
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.B2C:
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_SUPPORTED:
            return Constant.VISIBILITY_SETTING_TYPE.CUSTOMER;

        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.MEMBER_OF_PROJECT:
        case Constant.USER_PERMISSION_VIEW_KNOWLEDGE.GUEST_PARTNER:
            return Constant.VISIBILITY_SETTING_TYPE.PRIVATE;
        default:
            return Constant.VISIBILITY_SETTING_TYPE.CUSTOMER;
    }
}

export const checkedUsingChatAI = async (API, corporationDetail, projectInuseDetail) => {
    const contractType = corporationDetail?.contractType;
    switch (contractType) {
        case Constant.CORPORATION_CONTRACT_TYPE.PREMIUM: {
            const limitValue = corporationDetail?.limitValue;               
            return limitValue.usingChatAI;
        }
        case Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK:
        case Constant.CORPORATION_CONTRACT_TYPE.GUEST: {
            const corpInviter = await getCorporationById(API, corporationDetail?.inviter);
            if (Utils.isEmptyObject(corpInviter)) {
                return false;
            }
            const limitValueCorpInviter = corpInviter?.limitValue;
            return limitValueCorpInviter?.usingChatAI;
        }
        default:
            return false;
    }
};


/**
 * isDisplayProjectProfileMenu
 * @param {*} projectInuseDetail 
 * @returns 
 */
export const isDisplayProjectProfileMenu = (projectInuseDetail, isProjectManager) => {
    if (!projectInuseDetail || Utils.isEmptyObject(projectInuseDetail)) return false;

    if (
        projectInuseDetail.type === Constant.PROJECT_TYPE.BASE ||
        projectInuseDetail.type === Constant.PROJECT_TYPE.CUSTOMER ||
        Utils.isProjectGuest(projectInuseDetail)
    ) {
        return false;
    }

    return isProjectManager;
};

export const checkExpiredLinceseKey = async (
    API,
    corporationId,
    setIsLincenseKeyExpired
) => {
    if (!corporationId) {
        return;
    }
    let corporationDetail = await getCorporationById(API, corporationId);
    if (corporationDetail?.lincenseKeyStatus === Constant.LICENSE_KEY_STATUS.EXPIRED) {
        setIsLincenseKeyExpired(true);
    }
}